import React from 'react'

// components
import OpeningHours from '../OpeningHours/OpeningHours'
import SocialIcons from '../SocialIcons/SocialIcons'
import SalonMapEmbed from '../../../../components/SalonMapEmbed/SalonMapEmbed'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'

// types
import { ConfigResponse, SalonDetail } from '../../../../types/types'

// styles
import * as SC from './SalonDetailCardStyles'

// assets
import MapPinIcon from '../../../../assets/icons/MapPinIcon'
import MailIcon from '../../../../assets/icons/MailIcon'
import PhoneIcon from '../../../../assets/icons/PhoneIcon'
import ParkingIcon from '../../../../assets/icons/ParkingIcon'
import FolderIcon from '../../../../assets/icons/FolderIcon'

// utils
import { getPhoneNumber } from '../../../../utils/helper'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	countries: ConfigResponse['rolloutCountries']
	salonServices: string[]
	/* NOTE: SalonDetailCard used to be used for basic salon as well - now it's used only for
    profi salon, so this prop (and the content rendered when it's true) is not used anymore.
    Leaving it here only for potential future use. */
	isBasicSalon?: boolean
	salonData: SalonDetail
}

const SalonDetailCard = (props: Props) => {
	const { salonData, isBasicSalon, countries, salonServices } = props
	const socialLinksExist =
		salonData.socialLinkWebPage ||
		salonData.socialLinkFB ||
		salonData.socialLinkInstagram ||
		salonData.socialLinkPinterest ||
		salonData.socialLinkYoutube ||
		salonData.socialLinkTikTok

	const { messages } = useMessages()

	const lng = salonData.address?.longitude
	const lat = salonData.address?.latitude

	return (
		<SC.SalonDetailCardContainer aria-labelledby='salon-detail-card-heading'>
			<VisuallyHidden as='h2' id='salon-detail-card-heading'>
				{messages['Where to find us']}
			</VisuallyHidden>
			<SC.SalonCardContactInfo>
				{/* name */}
				{salonData.name && <SC.SalonCardTitle>{salonData.name}</SC.SalonCardTitle>}

				{/* email, phone, address */}
				{salonData.email && (
					<SC.InfoCardBox>
						<SC.IconBox>
							<MailIcon aria-hidden />
						</SC.IconBox>
						<SC.InfoCardBoxTitle>
							<SC.ContactLink href={`mailto:${salonData.email}`}>{salonData.email}</SC.ContactLink>
						</SC.InfoCardBoxTitle>
					</SC.InfoCardBox>
				)}
				{salonData.phones && salonData.phones.length > 0 && (
					<SC.InfoCardBox>
						<SC.IconBox>
							<PhoneIcon aria-hidden />
						</SC.IconBox>
						<SC.InfoCardBoxTitle>
							{salonData.phones.map((phone) => {
								const phoneNumber = getPhoneNumber(phone, countries)
								return (
									<SC.ContactLink key={phone.id} href={`tel:${phoneNumber}`}>
										{phoneNumber}
									</SC.ContactLink>
								)
							})}
						</SC.InfoCardBoxTitle>
					</SC.InfoCardBox>
				)}
				{salonData.formattedAddress && (
					<SC.InfoCardBox>
						<SC.IconBox>
							<MapPinIcon aria-hidden />
						</SC.IconBox>
						<SC.InfoCardBoxTitle>{salonData.formattedAddress}</SC.InfoCardBoxTitle>
					</SC.InfoCardBox>
				)}

				{/* services */}
				{isBasicSalon && salonServices && salonServices.length > 0 && (
					<>
						<SC.InfoCardBox>
							<SC.IconBox>
								<FolderIcon aria-hidden />
							</SC.IconBox>
							<SC.InfoCardBoxTitle>{messages.Services}</SC.InfoCardBoxTitle>
						</SC.InfoCardBox>
						<SC.ServicesContainer>
							{salonServices.map((service, index) => (
								<SC.Service key={index}>{service}</SC.Service>
							))}
						</SC.ServicesContainer>
					</>
				)}

				{/* opening hours */}
				{isBasicSalon && salonData.openingHoursData && (
					<SC.InfoCardBox>
						<OpeningHours openingHours={salonData.openingHoursData} darkBackground />
					</SC.InfoCardBox>
				)}

				{/* parking */}
				{!isBasicSalon && salonData.parkingNote && (
					<>
						<SC.InfoCardBox>
							<SC.IconBox>
								<ParkingIcon aria-hidden />
							</SC.IconBox>
							<SC.InfoCardBoxTitle>{messages.Parking}</SC.InfoCardBoxTitle>
						</SC.InfoCardBox>
						<SC.ParkingNote>{salonData.parkingNote}</SC.ParkingNote>
					</>
				)}

				{/* social links */}
				{!isBasicSalon && socialLinksExist && (
					<SC.SocialIconsContainer>
						<SocialIcons salonData={salonData} />
					</SC.SocialIconsContainer>
				)}
			</SC.SalonCardContactInfo>
			<SC.GoogleMapsContainer id={'google-map'}>
				<SalonMapEmbed lat={lat} lng={lng} formattedAddress={salonData.formattedAddress} salonName={salonData.name} />
			</SC.GoogleMapsContainer>
		</SC.SalonDetailCardContainer>
	)
}

export default SalonDetailCard
